import React, { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  useBillingReconciliationRecurringRules,
  useUpdateBillingReconciliationRecurringRule,
} from '../../hooks/api/useBillingReconciliationHooks.js';

const RecurringRulesPanel = ({ program = 'website' }) => {
  const [showDisabled, setShowDisabled] = useState(false);
  const query = useBillingReconciliationRecurringRules({
    program,
    status: showDisabled ? 'all' : 'active',
    limit: 250,
  });
  const updateRule = useUpdateBillingReconciliationRecurringRule();
  const rows = query.data?.rows || [];

  if (query.isLoading) {
    return (
      <Stack spacing={1}>
        <Skeleton height={48} />
        <Skeleton height={160} />
      </Stack>
    );
  }
  if (query.isError) return <Alert severity="error">Recurring match rules could not be loaded.</Alert>;

  return (
    <Stack spacing={1.5}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Stack spacing={0.25}>
            <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
              Recurring Match Rules
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Operator-promoted evidence links. Rules create suggestions only; they never overwrite current-round manual evidence.
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" color={rows.length ? 'info' : 'default'} label={`${rows.length} rules`} />
            <Button size="small" variant="outlined" onClick={() => setShowDisabled((value) => !value)}>
              {showDisabled ? 'Hide disabled' : 'Show disabled'}
            </Button>
          </Stack>
        </Stack>
      </Paper>
      {rows.length === 0 ? (
        <Alert severity="info">No recurring match rules yet. Promote a confirmed evidence action from a BAC drawer to create one.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
          <Table size="small" aria-label="recurring match rules">
            <TableHead>
              <TableRow>
                <TableCell>BAC</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Evidence</TableCell>
                <TableCell>Salesforce account</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{rule.bac}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>{rule.productCode || rule.productFamily}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{rule.productFamily}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ display: 'block' }}>{rule.sourceSystem}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                      {rule.orderItemId || rule.referenceId || '--'}
                    </Typography>
                  </TableCell>
                  <TableCell>{rule.salesforceAccountName || rule.salesforceAccountId || '--'}</TableCell>
                  <TableCell><Chip size="small" label={rule.status} color={rule.status === 'active' ? 'success' : 'default'} variant="outlined" /></TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={updateRule.isPending}
                      onClick={() => updateRule.mutate({
                        id: rule.id,
                        patch: { status: rule.status === 'active' ? 'disabled' : 'active' },
                      })}
                    >
                      {rule.status === 'active' ? 'Disable' : 'Enable'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
};

export default RecurringRulesPanel;
